'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

type CampaignStatus = 'active' | 'open' | 'completed' | 'draft' | 'paused';
type CampaignCategory = 'Beauty' | 'Tech' | 'Food' | 'Lifestyle' | 'Fashion' | 'Sport' | 'Gaming' | 'Education' | 'Automotive' | 'Travel';

interface InfluenceCampaign {
  id: string;
  brand: string;
  brandLogo: string;
  brandLogoAlt: string;
  title: string;
  description: string;
  category: CampaignCategory;
  budget: number;
  budgetDisplay: string;
  status: CampaignStatus;
  platforms: string[];
  targetAudience: string;
  location: string;
  ageRange: string;
  contentTypes: string[];
  creatorsNeeded: number;
  creatorsApplied: number;
  deadline: string;
  startDate: string;
  deliverables: string;
  minFollowers: string;
  minEngagement: string;
}

const mockCampaigns: InfluenceCampaign[] = [
{
  id: 'camp-1',
  brand: 'TECNO Mobile',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_161380ca9-1787085813595.png",
  brandLogoAlt: 'TECNO Mobile brand logo',
  title: 'Lancement Spark 20 Pro — Campagne Nationale',
  description: 'Nous recherchons des créateurs Tech & Lifestyle pour promouvoir le lancement du TECNO Spark 20 Pro en Algérie. Contenu authentique, démonstrations produit, unboxing et reviews.',
  category: 'Tech',
  budget: 2500000,
  budgetDisplay: '2 500 000 DA',
  status: 'open',
  platforms: ['Instagram', 'TikTok'],
  targetAudience: 'Hommes & Femmes 18–35 ans',
  location: 'Algérie (national)',
  ageRange: '18–35',
  contentTypes: ['Reel', 'TikTok', 'Story', 'Review'],
  creatorsNeeded: 8,
  creatorsApplied: 14,
  deadline: '25 Aoû 2026',
  startDate: '1 Sep 2026',
  deliverables: '5 Reels + 10 Stories + 3 TikTok',
  minFollowers: '50K',
  minEngagement: '5%'
},
{
  id: 'camp-2',
  brand: 'Yassir',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1770aff64-1787085812624.png",
  brandLogoAlt: 'Yassir app brand logo',
  title: 'Yassir Express — Micro-Influenceurs Alger',
  description: 'Campagne ciblée sur Alger pour promouvoir Yassir Express. Nous cherchons des micro-influenceurs avec une audience locale forte et un engagement élevé.',
  category: 'Lifestyle',
  budget: 800000,
  budgetDisplay: '800 000 DA',
  status: 'open',
  platforms: ['Instagram', 'Snapchat'],
  targetAudience: 'Femmes 20–30 ans, Alger',
  location: 'Alger',
  ageRange: '20–30',
  contentTypes: ['Story', 'Reel', 'UGC'],
  creatorsNeeded: 12,
  creatorsApplied: 7,
  deadline: '30 Aoû 2026',
  startDate: '5 Sep 2026',
  deliverables: '3 Stories + 1 Reel par créateur',
  minFollowers: '10K',
  minEngagement: '7%'
},
{
  id: 'camp-3',
  brand: 'Samsung Algeria',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_141d57015-1787085812998.png",
  brandLogoAlt: 'Samsung Algeria brand logo',
  title: 'Galaxy S25 — Lifestyle Campaign',
  description: 'Campagne lifestyle premium pour le Galaxy S25. Nous cherchons des créateurs avec une esthétique soignée pour intégrer le smartphone dans leur quotidien de façon naturelle.',
  category: 'Tech',
  budget: 5000000,
  budgetDisplay: '5 000 000 DA',
  status: 'active',
  platforms: ['Instagram', 'TikTok', 'YouTube'],
  targetAudience: 'Hommes & Femmes 25–40 ans',
  location: 'Algérie (national)',
  ageRange: '25–40',
  contentTypes: ['Reel', 'YouTube Video', 'Story', 'TikTok'],
  creatorsNeeded: 5,
  creatorsApplied: 5,
  deadline: '20 Aoû 2026',
  startDate: '28 Aoû 2026',
  deliverables: '2 Reels + 1 YouTube + 5 Stories',
  minFollowers: '100K',
  minEngagement: '4%'
},
{
  id: 'camp-4',
  brand: 'Djezzy',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_107ee22a9-1787085813476.png",
  brandLogoAlt: 'Djezzy telecom brand logo',
  title: 'Djezzy 5G — Campagne Jeunes',
  description: 'Promouvoir le réseau 5G Djezzy auprès des jeunes algériens. Contenu créatif montrant la vitesse et les possibilités de la 5G dans la vie quotidienne.',
  category: 'Tech',
  budget: 1500000,
  budgetDisplay: '1 500 000 DA',
  status: 'draft',
  platforms: ['TikTok', 'Instagram'],
  targetAudience: 'Jeunes 16–28 ans',
  location: 'Algérie (national)',
  ageRange: '16–28',
  contentTypes: ['TikTok', 'Reel', 'Story'],
  creatorsNeeded: 10,
  creatorsApplied: 0,
  deadline: '10 Sep 2026',
  startDate: '20 Sep 2026',
  deliverables: '2 TikTok + 2 Reels + 3 Stories',
  minFollowers: '20K',
  minEngagement: '6%'
},
{
  id: 'camp-5',
  brand: 'Coca-Cola Algeria',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fdc69160-1787085813803.png",
  brandLogoAlt: 'Coca-Cola Algeria brand logo',
  title: 'Ramadan 2027 — Moments de Partage',
  description: 'Campagne Ramadan autour du partage et de la convivialité. Créateurs Food & Lifestyle pour des contenus chaleureux et authentiques autour des moments en famille.',
  category: 'Food',
  budget: 3200000,
  budgetDisplay: '3 200 000 DA',
  status: 'completed',
  platforms: ['Instagram', 'TikTok', 'Snapchat'],
  targetAudience: 'Familles algériennes 25–45 ans',
  location: 'Algérie (national)',
  ageRange: '25–45',
  contentTypes: ['Reel', 'Story', 'TikTok', 'UGC'],
  creatorsNeeded: 15,
  creatorsApplied: 15,
  deadline: '1 Mar 2027',
  startDate: '10 Mar 2027',
  deliverables: '3 Reels + 5 Stories + 2 TikTok',
  minFollowers: '30K',
  minEngagement: '5%'
},
{
  id: 'camp-6',
  brand: 'L\'Oréal Algeria',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_153bb38cb-1787085813409.png",
  brandLogoAlt: "L'Oréal Algeria beauty brand logo",
  title: 'Nouvelle Gamme Skincare — Beauty Creators',
  description: 'Lancement d\'une nouvelle gamme skincare adaptée à la peau algérienne. Nous cherchons des créatrices Beauty avec une expertise en soins de la peau.',
  category: 'Beauty',
  budget: 1800000,
  budgetDisplay: '1 800 000 DA',
  status: 'open',
  platforms: ['Instagram', 'TikTok'],
  targetAudience: 'Femmes 18–35 ans',
  location: 'Alger, Oran, Constantine',
  ageRange: '18–35',
  contentTypes: ['Tutorial', 'Reel', 'Story', 'Review'],
  creatorsNeeded: 6,
  creatorsApplied: 9,
  deadline: '5 Sep 2026',
  startDate: '15 Sep 2026',
  deliverables: '2 Tutorials + 3 Reels + 5 Stories',
  minFollowers: '25K',
  minEngagement: '6%'
}];


const STATUS_CONFIG: Record<CampaignStatus, {label: string;color: string;dot: string;}> = {
  open: { label: 'Ouvert', color: 'bg-positive/15 text-positive border-positive/30', dot: 'bg-positive' },
  active: { label: 'En cours', color: 'bg-primary/15 text-primary border-primary/30', dot: 'bg-primary' },
  completed: { label: 'Terminé', color: 'bg-muted text-muted-foreground border-border', dot: 'bg-muted-foreground' },
  draft: { label: 'Brouillon', color: 'bg-warning/15 text-warning border-warning/30', dot: 'bg-warning' },
  paused: { label: 'Pausé', color: 'bg-muted text-muted-foreground/60 border-border', dot: 'bg-muted-foreground/60' }
};

const CATEGORIES: (CampaignCategory | 'all')[] = ['all', 'Beauty', 'Tech', 'Food', 'Lifestyle', 'Fashion', 'Sport', 'Gaming', 'Education', 'Automotive', 'Travel'];
const BUDGET_RANGES = [
{ label: 'Tous les budgets', min: 0, max: Infinity },
{ label: '< 500K DA', min: 0, max: 500000 },
{ label: '500K – 1M DA', min: 500000, max: 1000000 },
{ label: '1M – 3M DA', min: 1000000, max: 3000000 },
{ label: '3M – 5M DA', min: 3000000, max: 5000000 },
{ label: '> 5M DA', min: 5000000, max: Infinity }];


function CreateCampaignModal({ onClose, onSave }: {onClose: () => void;onSave: (c: InfluenceCampaign) => void;}) {
  const [form, setForm] = useState({
    brand: '', title: '', description: '', category: 'Lifestyle' as CampaignCategory,
    budget: '', platforms: [] as string[], targetAudience: '', location: '',
    ageRange: '', creatorsNeeded: '', deadline: '', deliverables: '',
    minFollowers: '', minEngagement: '', status: 'draft' as CampaignStatus
  });

  const togglePlatform = (p: string) => {
    setForm((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(p) ? prev.platforms.filter((x) => x !== p) : [...prev.platforms, p]
    }));
  };

  const handleSave = () => {
    if (!form.brand || !form.title || !form.budget) return;
    const budgetNum = parseInt(form.budget.replace(/\D/g, '')) || 0;
    const newCamp: InfluenceCampaign = {
      id: `camp-${Date.now()}`,
      brand: form.brand,
      brandLogo: 'https://img.rocket.new/generatedImages/rocket_gen_img_19b3fe9e0-1773215727744.png',
      brandLogoAlt: `${form.brand} brand logo`,
      title: form.title,
      description: form.description,
      category: form.category,
      budget: budgetNum,
      budgetDisplay: `${budgetNum.toLocaleString('fr-DZ')} DA`,
      status: form.status,
      platforms: form.platforms,
      targetAudience: form.targetAudience,
      location: form.location,
      ageRange: form.ageRange,
      contentTypes: [],
      creatorsNeeded: parseInt(form.creatorsNeeded) || 1,
      creatorsApplied: 0,
      deadline: form.deadline,
      startDate: '',
      deliverables: form.deliverables,
      minFollowers: form.minFollowers,
      minEngagement: form.minEngagement
    };
    onSave(newCamp);
    onClose();
  };

  const allPlatforms = ['Instagram', 'TikTok', 'YouTube', 'Snapchat', 'Facebook', 'LinkedIn'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="RocketLaunchIcon" size={16} className="text-primary" />
            </div>
            <h2 className="text-base font-700 text-foreground">Nouvelle campagne d'influence</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Marque *</label>
              <input value={form.brand} onChange={(e) => setForm((p) => ({ ...p, brand: e.target.value }))}
              placeholder="Nom de la marque" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Catégorie</label>
              <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value as CampaignCategory }))}
              className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors">
                {CATEGORIES.filter((c) => c !== 'all').map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Titre de la campagne *</label>
            <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="Ex: Lancement produit — Campagne nationale" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Description</label>
            <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Décrivez les objectifs, le brief créatif, les attentes..." rows={3}
            className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Budget (DA) *</label>
              <input value={form.budget} onChange={(e) => setForm((p) => ({ ...p, budget: e.target.value }))}
              placeholder="Ex: 1500000" type="number" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Créateurs recherchés</label>
              <input value={form.creatorsNeeded} onChange={(e) => setForm((p) => ({ ...p, creatorsNeeded: e.target.value }))}
              placeholder="Ex: 5" type="number" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Localisation cible</label>
              <input value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
              placeholder="Ex: Alger, Oran..." className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Tranche d'âge</label>
              <input value={form.ageRange} onChange={(e) => setForm((p) => ({ ...p, ageRange: e.target.value }))}
              placeholder="Ex: 18–35" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Followers minimum</label>
              <input value={form.minFollowers} onChange={(e) => setForm((p) => ({ ...p, minFollowers: e.target.value }))}
              placeholder="Ex: 10K" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Engagement minimum</label>
              <input value={form.minEngagement} onChange={(e) => setForm((p) => ({ ...p, minEngagement: e.target.value }))}
              placeholder="Ex: 5%" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-2 uppercase tracking-wide">Plateformes</label>
            <div className="flex flex-wrap gap-2">
              {allPlatforms.map((p) =>
              <button key={p} onClick={() => togglePlatform(p)}
              className={`px-3 py-1.5 rounded-lg border text-xs font-600 transition-all ${
              form.platforms.includes(p) ? 'bg-primary/15 text-primary border-primary/40' : 'bg-muted text-muted-foreground border-border hover:border-primary/40'}`
              }>
                  {p}
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Date limite candidature</label>
              <input type="date" value={form.deadline} onChange={(e) => setForm((p) => ({ ...p, deadline: e.target.value }))}
              className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Statut</label>
              <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as CampaignStatus }))}
              className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors">
                <option value="draft">Brouillon</option>
                <option value="open">Ouvrir aux candidatures</option>
                <option value="active">Activer</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Livrables attendus</label>
            <input value={form.deliverables} onChange={(e) => setForm((p) => ({ ...p, deliverables: e.target.value }))}
            placeholder="Ex: 3 Reels + 5 Stories + 2 TikTok" className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-600 text-muted-foreground hover:bg-muted transition-all">Annuler</button>
          <button onClick={handleSave} disabled={!form.brand || !form.title || !form.budget}
          className="px-5 py-2 rounded-xl text-sm font-700 bg-primary text-primary-foreground hover:bg-primary/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            Créer la campagne
          </button>
        </div>
      </div>
    </div>);

}

function CampaignCard({ campaign, onDelete }: {campaign: InfluenceCampaign;onDelete: (id: string) => void;}) {
  const statusCfg = STATUS_CONFIG[campaign.status];
  const fillPct = campaign.creatorsNeeded > 0 ? Math.min(100, Math.round(campaign.creatorsApplied / campaign.creatorsNeeded * 100)) : 0;

  return (
    <div className="bg-card border border-border rounded-2xl p-5 hover:border-primary/30 transition-all group flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-muted">
            <AppImage src={campaign.brandLogo} alt={campaign.brandLogoAlt} className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-600 text-muted-foreground truncate">{campaign.brand}</p>
            <p className="text-sm font-700 text-foreground leading-tight">{campaign.title}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-600 border ${statusCfg.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
            {statusCfg.label}
          </span>
          <span className="px-2 py-0.5 rounded-md text-xs font-600 bg-muted text-muted-foreground border border-border">
            {campaign.category}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{campaign.description}</p>

      {/* Budget highlight */}
      <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/15 rounded-xl">
        <Icon name="CurrencyDollarIcon" size={16} className="text-primary flex-shrink-0" />
        <div>
          <p className="text-base font-800 text-primary">{campaign.budgetDisplay}</p>
          <p className="text-xs text-muted-foreground">Budget total campagne</p>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Icon name="MapPinIcon" size={12} className="flex-shrink-0" />
          <span className="truncate">{campaign.location}</span>
        </div>
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Icon name="UserGroupIcon" size={12} className="flex-shrink-0" />
          <span className="truncate">{campaign.targetAudience}</span>
        </div>
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Icon name="ChartBarIcon" size={12} className="flex-shrink-0" />
          <span>Min. {campaign.minFollowers} followers</span>
        </div>
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Icon name="SparklesIcon" size={12} className="flex-shrink-0" />
          <span>Engagement ≥ {campaign.minEngagement}</span>
        </div>
      </div>

      {/* Platforms */}
      <div className="flex flex-wrap gap-1.5">
        {campaign.platforms.map((p) =>
        <span key={p} className="px-2 py-0.5 rounded-md text-xs font-600 bg-muted text-muted-foreground border border-border">{p}</span>
        )}
      </div>

      {/* Creators progress */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-muted-foreground">Candidatures</span>
          <span className="text-xs font-700 text-foreground">{campaign.creatorsApplied} / {campaign.creatorsNeeded}</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${fillPct}%` }} />
          
        </div>
      </div>

      {/* Deliverables */}
      {campaign.deliverables &&
      <div className="flex items-start gap-2 p-2.5 bg-muted/50 rounded-lg">
          <Icon name="ClipboardDocumentListIcon" size={13} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">{campaign.deliverables}</p>
        </div>
      }

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon name="CalendarIcon" size={12} />
          <span>Deadline: {campaign.deadline || '—'}</span>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
            <Icon name="PencilSquareIcon" size={14} />
          </button>
          <button onClick={() => onDelete(campaign.id)} className="p-1.5 rounded-lg hover:bg-negative/10 text-muted-foreground hover:text-negative transition-all">
            <Icon name="TrashIcon" size={14} />
          </button>
        </div>
      </div>
    </div>);

}

export default function InfluenceCampaignsPage() {
  const [campaigns, setCampaigns] = useState<InfluenceCampaign[]>(mockCampaigns);
  const [showCreate, setShowCreate] = useState(false);
  const [filterCategory, setFilterCategory] = useState<CampaignCategory | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<CampaignStatus | 'all'>('all');
  const [filterBudget, setFilterBudget] = useState(0);
  const [search, setSearch] = useState('');

  const budgetRange = BUDGET_RANGES[filterBudget];

  const filtered = campaigns.filter((c) => {
    if (filterCategory !== 'all' && c.category !== filterCategory) return false;
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    if (c.budget < budgetRange.min || c.budget > budgetRange.max) return false;
    if (search && !c.title.toLowerCase().includes(search.toLowerCase()) && !c.brand.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: campaigns.length,
    open: campaigns.filter((c) => c.status === 'open').length,
    active: campaigns.filter((c) => c.status === 'active').length,
    totalBudget: campaigns.reduce((sum, c) => sum + c.budget, 0)
  };

  return (
    <AppLayout currentPath="/influence-campaigns">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-800 text-foreground">Campagnes d'Influence</h1>
            <p className="text-sm text-muted-foreground mt-1">Créez et gérez vos campagnes — recherchez par budget, catégorie et plateforme</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-700 hover:bg-primary/90 transition-all active:scale-95 flex-shrink-0">
            
            <Icon name="PlusIcon" size={16} />
            Nouvelle campagne
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
          { label: 'Total campagnes', value: stats.total, icon: 'RocketLaunchIcon', color: 'text-foreground' },
          { label: 'Ouvertes', value: stats.open, icon: 'MegaphoneIcon', color: 'text-positive' },
          { label: 'En cours', value: stats.active, icon: 'BoltIcon', color: 'text-primary' },
          { label: 'Budget total', value: `${(stats.totalBudget / 1000000).toFixed(1)}M DA`, icon: 'CurrencyDollarIcon', color: 'text-warning' }].
          map((kpi) =>
          <div key={kpi.label} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={18} className={kpi.color} />
              </div>
              <div>
                <p className={`text-xl font-800 ${kpi.color}`}>{kpi.value}</p>
                <p className="text-xs text-muted-foreground">{kpi.label}</p>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="FunnelIcon" size={14} className="text-muted-foreground" />
            <span className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Filtres</span>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-48">
              <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une campagne ou marque..."
                className="w-full bg-muted border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors" />
              
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as CampaignCategory | 'all')}
              className="bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors">
              
              <option value="all">Toutes catégories</option>
              {CATEGORIES.filter((c) => c !== 'all').map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as CampaignStatus | 'all')}
              className="bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors">
              
              <option value="all">Tous les statuts</option>
              {(Object.keys(STATUS_CONFIG) as CampaignStatus[]).map((s) =>
              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
              )}
            </select>
            <select
              value={filterBudget}
              onChange={(e) => setFilterBudget(parseInt(e.target.value))}
              className="bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors">
              
              {BUDGET_RANGES.map((r, i) =>
              <option key={r.label} value={i}>{r.label}</option>
              )}
            </select>
          </div>
          {/* Budget quick-filter pills */}
          <div className="flex flex-wrap gap-2">
            {BUDGET_RANGES.map((r, i) =>
            <button
              key={r.label}
              onClick={() => setFilterBudget(i)}
              className={`px-3 py-1 rounded-full text-xs font-600 border transition-all ${
              filterBudget === i ? 'bg-primary/15 text-primary border-primary/40' : 'bg-muted text-muted-foreground border-border hover:border-primary/30'}`
              }>
              
                {r.label}
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            <span className="font-700 text-foreground">{filtered.length}</span> campagne{filtered.length !== 1 ? 's' : ''} trouvée{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Campaigns Grid */}
        {filtered.length === 0 ?
        <div className="bg-card border border-border rounded-2xl p-12 text-center">
            <Icon name="RocketLaunchIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-base font-600 text-foreground mb-1">Aucune campagne trouvée</p>
            <p className="text-sm text-muted-foreground">Modifiez vos filtres ou créez une nouvelle campagne</p>
          </div> :

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((campaign) =>
          <CampaignCard
            key={campaign.id}
            campaign={campaign}
            onDelete={(id) => setCampaigns((prev) => prev.filter((c) => c.id !== id))} />

          )}
          </div>
        }
      </div>

      {showCreate &&
      <CreateCampaignModal
        onClose={() => setShowCreate(false)}
        onSave={(c) => setCampaigns((prev) => [c, ...prev])} />

      }
    </AppLayout>);

}